import type { Dictionary } from '@ngrx/entity';
import { createSelector, type MemoizedSelector } from '@ngrx/store';
import type { Category, CategoryId, ResourceType } from '../../../interfaces/Category.interface';
import type { AppState } from '../../app.state';
import { selectEntitiesState } from '../entities-state.selector';
import { categoryAdapter, MapKey, type State } from './categories.state';

export const selectCategoriesState: MemoizedSelector<AppState, State> = createSelector(
    selectEntitiesState,
    entitiesState => entitiesState.categories
);

export const selectIsPending: MemoizedSelector<AppState, boolean> = createSelector(
    selectCategoriesState,
    (state: State) => state.isPending
);
export const selectIsLoaded: MemoizedSelector<AppState, boolean> = createSelector(selectCategoriesState, (state: State) => state.isLoaded);

export const selectAllCategories: (state: AppState) => Category[] = categoryAdapter.getSelectors(selectCategoriesState).selectAll;

export const selectCategoriesByResourceType: (resourceType: ResourceType) => MemoizedSelector<AppState, Category[]> = (
    resourceType: ResourceType
) => categoryAdapter.getSelectors(selectCategoriesState).selectEntitiesByMappedId(MapKey.BY_RESOURCE_TYPE, resourceType);

export const selectCategoriesByResourceTypes: (resourceTypes: ResourceType[]) => MemoizedSelector<AppState, Category[]> = (
    resourceTypes: ResourceType[]
) => categoryAdapter.getSelectors(selectCategoriesState).selectEntitiesByMappedIds(MapKey.BY_RESOURCE_TYPE, resourceTypes);

export const selectCategoryIdsByResourceType: (resourceType: ResourceType) => MemoizedSelector<AppState, CategoryId[]> = (
    resourceType: ResourceType
) => createSelector(selectCategoriesByResourceType(resourceType), categories => categories.map(category => category.categoryId));

export const selectCategoriesMap: (state: AppState) => Dictionary<Category> =
    categoryAdapter.getSelectors(selectCategoriesState).selectEntities;

export const selectCategoryById: (categoryId: CategoryId) => MemoizedSelector<AppState, Category | null> = (categoryId: CategoryId) =>
    createSelector(selectCategoriesMap, categoriesMap => categoriesMap[categoryId] ?? null);

export const selectCategoryIds: (state: AppState) => CategoryId[] = categoryAdapter.getSelectors(selectCategoriesState).selectIds as (
    state: AppState
) => CategoryId[];
